import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Loader2, MapPin, User, Linkedin, Twitter, Mail, Globe,
  Layers, Facebook, Instagram, Youtube,
} from 'lucide-react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import RichContent from '../components/RichContent';
import { getPerson, listPersonBlog, listVerticals, resolveFileUrl } from '../services/api';
import { normalizeSocial } from '../utils/socials';

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CAT_LABEL = {
  board: 'Board of Directors',
  management: 'Management',
  key_personnel: 'Key Personnel',
};

const PersonPage = () => {
  const { slug } = useParams();
  const [person, setPerson] = useState(null);
  const [posts, setPosts] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      getPerson(slug),
      listPersonBlog(slug).catch(() => ({ data: [] })),
      listVerticals().catch(() => ({ data: [] })),
    ])
      .then(([p, b, v]) => {
        setPerson(p);
        setPosts(b.data || []);
        setVerticals(v.data || []);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/leadership" replace />;

  const personVerticals = person
    ? verticals.filter((v) => (person.vertical_slugs || []).includes(v.slug))
    : [];

  const socialLinks = person?.socials || {};

  return (
    <Layout>
      {loading || !person ? (
        <div className="bg-[#e2ede6] py-32 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <>
          <PageHero
            eyebrow={CAT_LABEL[person.category] || 'Team'}
            title={person.name}
            description={person.short_desc}
            image={
              resolveFileUrl(person.photo_url) ||
              'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1600&q=80'
            }
          />
          <section className="bg-[#e2ede6] py-16 lg:py-24" data-testid="person-page">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                {/* Left — Profile card */}
                <aside className="lg:col-span-1">
                  <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm sticky top-24">
                    <div className="aspect-square rounded-xl overflow-hidden bg-[#e2ede6] mb-4">
                      {person.photo_url ? (
                        <img src={resolveFileUrl(person.photo_url)} alt={person.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#0b1a2e]/25">
                          <User className="w-16 h-16" />
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#0f5c3d] font-semibold">
                      {person.designation}
                    </div>
                    <h1 className="mt-1 text-xl font-semibold text-[#0b1a2e]">{person.name}</h1>
                    {person.location && (
                      <div className="mt-2 inline-flex items-center gap-1 text-sm text-[#0b1a2e]/70">
                        <MapPin className="w-4 h-4" /> {person.location}
                      </div>
                    )}
                    {/* Socials */}
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      {socialLinks.linkedin && (
                        <a href={normalizeSocial('linkedin', socialLinks.linkedin)} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="person-social-linkedin" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Linkedin className="w-4 h-4" /></a>
                      )}
                      {socialLinks.twitter && (
                        <a href={normalizeSocial('twitter', socialLinks.twitter)} target="_blank" rel="noreferrer" aria-label="X (Twitter)" data-testid="person-social-twitter" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Twitter className="w-4 h-4" /></a>
                      )}
                      {socialLinks.facebook && (
                        <a href={normalizeSocial('facebook', socialLinks.facebook)} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="person-social-facebook" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Facebook className="w-4 h-4" /></a>
                      )}
                      {socialLinks.instagram && (
                        <a href={normalizeSocial('instagram', socialLinks.instagram)} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="person-social-instagram" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Instagram className="w-4 h-4" /></a>
                      )}
                      {socialLinks.youtube && (
                        <a href={normalizeSocial('youtube', socialLinks.youtube)} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="person-social-youtube" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Youtube className="w-4 h-4" /></a>
                      )}
                      {socialLinks.email && (
                        <a href={normalizeSocial('email', socialLinks.email)} aria-label="Email" data-testid="person-social-email" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Mail className="w-4 h-4" /></a>
                      )}
                      {socialLinks.website && (
                        <a href={normalizeSocial('website', socialLinks.website)} target="_blank" rel="noreferrer" aria-label="Website" data-testid="person-social-website" className="w-8 h-8 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d] hover:text-white flex items-center justify-center text-[#0f5c3d] transition"><Globe className="w-4 h-4" /></a>
                      )}
                    </div>
                    {personVerticals.length > 0 && (
                      <div className="mt-6 pt-5 border-t border-black/5">
                        <div className="text-[10px] uppercase tracking-widest text-[#0f5c3d] font-semibold mb-3 inline-flex items-center gap-1.5">
                          <Layers className="w-3.5 h-3.5" /> Business verticals
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {personVerticals.map((v) => (
                            <Link
                              key={v.slug}
                              to={`/verticals/${v.slug}`}
                              data-testid="person-vertical-chip"
                              className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#0f5c3d]/8 text-[#0f5c3d] hover:bg-[#0f5c3d] hover:text-white transition"
                            >
                              {v.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </aside>

                {/* Right — Bio + Blog */}
                <div className="lg:col-span-2 space-y-10">
                  {person.long_desc && (
                    <div className="bg-white rounded-2xl border border-black/5 p-6 lg:p-8 shadow-sm">
                      <h2
                        className="text-2xl text-[#0b1a2e] mb-5 border-l-4 border-[#0f5c3d] pl-4"
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
                      >
                        About {person.name.split(' ')[0]}
                      </h2>
                      <RichContent text={person.long_desc} />
                    </div>
                  )}

                  <div>
                    <div className="flex items-baseline justify-between mb-5">
                      <h2
                        className="text-2xl text-[#0b1a2e]"
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
                      >
                        Personal Blog
                      </h2>
                      <span className="text-xs text-[#0b1a2e]/50">
                        {posts.length} {posts.length === 1 ? 'post' : 'posts'}
                      </span>
                    </div>
                    {posts.length === 0 ? (
                      <div className="bg-white/60 rounded-2xl border border-black/5 p-10 text-center text-sm text-[#0b1a2e]/50">
                        {person.name.split(' ')[0]} hasn't published any posts yet.
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {posts.map((post) => (
                          <Link
                            key={post.id}
                            to={`/people/${person.slug}/blog/${post.slug}`}
                            data-testid="blog-post-card"
                            className="group flex flex-col sm:flex-row gap-5 bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-xl transition-all"
                          >
                            <div className="sm:w-56 sm:h-auto h-40 shrink-0 aspect-[16/10] sm:aspect-auto overflow-hidden bg-[#e2ede6]">
                              {post.cover_image ? (
                                <img src={resolveFileUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-tr from-[#0b1a2e] to-[#0f5c3d]" />
                              )}
                            </div>
                            <div className="p-5 flex-1">
                              <div className="text-xs font-semibold tracking-widest uppercase text-[#0f5c3d]">
                                {formatDate(post.published_at)}
                              </div>
                              <h3 className="mt-1.5 text-xl text-[#0b1a2e] group-hover:text-[#0f5c3d] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
                                {post.title}
                              </h3>
                              <p className="mt-2 text-sm text-[#0b1a2e]/70 line-clamp-2">{post.excerpt}</p>
                              <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#0f5c3d]">
                                Read post <ArrowRight className="w-3.5 h-3.5" />
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-4">
                    <Link
                      to="/leadership"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0f5c3d]/25 text-[#0b1a2e] text-sm hover:bg-white transition"
                    >
                      <ArrowLeft className="w-4 h-4" /> All leadership
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </Layout>
  );
};

export default PersonPage;
